/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useState, useContext} from 'react'
import PropTypes from 'prop-types'
import {useIntl, FormattedMessage} from 'react-intl'
import {Link as RouteLink} from 'react-router-dom'

import {
    Box,
    Divider,
    Flex,
    HStack,
    Stack,
    Text,
    Button,
    useBreakpointValue,
    useTheme
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import {useAuthHelper, AuthHelpers} from '@salesforce/commerce-sdk-react'
import {useSiteCustomPreferences} from '../../hooks/use-site-custom-preferences'
import {useCurrentCustomer} from '../../hooks/use-current-customer'
import {MobileListMenuPopover} from '../mobile-list-menu/mobile-list-menu-popover'
import {mainMenuItemSelected} from '../../integrations/segment/app/page/home'
import {SegmentContext} from '../../integrations/segment/app/contexts'
import GlobalContentSlotHtml from '../content-slot/global-content-slot-html'
import {ChevronRightIcon, ChevronLeftIcon} from '../icons'
import UserIcon from '../header/UserIcon'
import {categoryUrlBuilder} from '../../utils/url'
import {setSessionJSONItem, buildRedirectURI} from '../../utils/utils'
import {getSSOCookie} from '../../utils/custom-api-utils'

const MAXIMUM_NUMBER_COLUMNS = 5

/**
 * This is the navigation component used for mobile devices. Holds the site navigation,
 * providing users with a way to access all product categories and other important pages.
 * The submenus are open when the user clicks on the trigger and slides in from right to left.
 *
 * @param maxColumns The maximum number of columns that we want to use per row inside the ListMenu.
 * @param root
 */
const MobileListMenu = ({
    root,
    contentComponent,
    itemsKey,
    itemsCountKey,
    maxColumns = MAXIMUM_NUMBER_COLUMNS,
    isOpen,
    handleMenuClick
}) => {
    const theme = useTheme()
    const segmentContext = useContext(SegmentContext)
    const navigate = useNavigation()
    const [ariaBusy, setAriaBusy] = useState(true)
    const [activeLink, setActiveLink] = useState()
    const [activeCategoryId, setActiveCategoryId] = useState(null)
    const [selectedItem, setSelectedItem] = useState(null)
    const [searchHeader, setSearchHeader] = useState(null)
    const [searchTabletHeader, setSearchTabletHeader] = useState(null)
    const [showAccountMenu, setShowAccountMenu] = useState(false)
    const intl = useIntl()
    const isMobile = useBreakpointValue({base: true, sm: false, md: false, lg: false})
    const {baseStyle} = theme.components.ListMenu
    const items = root?.[itemsKey]
    const authorizeIDP = useAuthHelper(AuthHelpers.AuthorizeIDP)
    const logout = useAuthHelper(AuthHelpers.Logout)
    const appOrigin = useAppOrigin()
    const configs = getConfig()
    const {locale, site} = useMultiSite()
    const {data: customer} = useCurrentCustomer()
    const redirectPath = configs?.app?.login?.social?.redirectURI || ''
    const redirectURI = buildRedirectURI(appOrigin, redirectPath, site, locale)
    const {getcustomPreferencevalue} = useSiteCustomPreferences()
    const ssoEnable = getcustomPreferencevalue('pwaSSOEnable')
    const rightStufOrderStatusURL = getcustomPreferencevalue('rsOrderStatusUrl')
    const silentLoginCookie = configs?.app?.library?.silentLoginCookie
    const onSocialLoginClick = async () => {
        try {
            // Save the path where the user logged in
            setSessionJSONItem('returnToPage', window.location.pathname)
            await authorizeIDP.mutateAsync({
                hint: configs?.app?.login?.social?.ssoIDPName,
                redirectURI: redirectURI
            })
        } catch (error) {
            console.error('Error in onSocialLoginClick:', error)
        }
    }
    const onSocialLoginLogout = async () => {
        try {
            await logout.mutateAsync()
            await getSSOCookie(appOrigin, silentLoginCookie, 'delete')
        } catch (error) {
            logger.error('Error in onSocialLoginLogout', {
                namespace: 'header.logout',
                additionalProperties: {error: error.message}
            })
        }
    }
    const registeredUserData = [
        {
            to: '/account',
            message: (
                <FormattedMessage
                    id="header.mobile.my_account"
                    defaultMessage="My Account Overview"
                />
            )
        },
        {
            to: '/wishlist#Wishlist',
            message: (
                <FormattedMessage id="global.account.link.wishlist" defaultMessage="Wishlist" />
            )
        },
        {
            to: '/orders',
            message: (
                <FormattedMessage
                    id="global.account.link.order_history"
                    defaultMessage="Order History"
                />
            )
        },
        {
            to: rightStufOrderStatusURL,
            isExternal: true,
            message: (
                <FormattedMessage
                    id="header.rightstuff_order_status"
                    defaultMessage="Rightstuf Order Status"
                />
            )
        }
    ]

    useEffect(() => {
        setAriaBusy(false)
    }, [])

    const handleOpenModal = (item) => {
        setSelectedItem(item)
        setActiveCategoryId(item?.id)
    }

    const handleCloseModal = () => {
        setSelectedItem(null)
        setActiveCategoryId(null)
    }

    const handleAccountMenuToggle = (show) => {
        setShowAccountMenu(show)
    }

    useEffect(() => {
        // We are handling the header height dynamically instead of using static values in CSS for mobile and tablet devices.
        const header = document.querySelector('.cr-header')?.offsetHeight
        const headerMobile = document.querySelector('.header-block')?.offsetHeight
        const svodHeight = document.querySelector('.header-promo-and-svod-block')?.offsetHeight
        const search = document.querySelector('.search-input')?.offsetHeight

        const headerHeight = headerMobile + svodHeight - search
        setSearchHeader(headerHeight)
        setSearchTabletHeader(header)

        if (!isOpen) {
            handleCloseModal()
            setShowAccountMenu(false)
        }
    }, [isOpen])

    const renderSlideMenu = (isAccountMenu = false, item = null) => {
        const menuItems = isAccountMenu ? registeredUserData : item?.categories || []
        const menuTitle = isAccountMenu ? 'My Account' : selectedItem?.name || 'View menu'
        const handleClose = isAccountMenu ? () => handleAccountMenuToggle(false) : handleCloseModal

        return (
            <Flex
                className={`l1-category-list ${
                    isAccountMenu
                        ? showAccountMenu
                            ? 'show'
                            : ''
                        : activeCategoryId == item?.id
                        ? 'show'
                        : ''
                }`}
                direction={'column'}
                top={
                    isOpen
                        ? {
                              base: `${searchHeader}px`,
                              sm: `${searchTabletHeader}px`
                          }
                        : `${searchHeader}px`
                }
                height={
                    isOpen
                        ? {
                              base: `calc(100vh - ${searchHeader}px)`,
                              sm: `calc(100vh - ${searchTabletHeader}px)`
                          }
                        : ``
                }
            >
                <Flex
                    direction={'row'}
                    alignItems={'end'}
                    justifyContent={'space-between'}
                    paddingBottom={'14px'}
                >
                    {isAccountMenu ? (
                        <Text fontWeight="bold">
                            <FormattedMessage
                                id="header.popover.title.my_account"
                                defaultMessage="My Account"
                            />
                        </Text>
                    ) : (
                        <Link as={RouteLink} to={categoryUrlBuilder(item)}>
                            {menuTitle}
                        </Link>
                    )}

                    <Flex
                        alignItems={'center'}
                        onClick={(e) => {
                            e.preventDefault()
                            handleClose()
                        }}
                        style={{cursor: 'pointer'}}
                        paddingTop={'14px'}
                    >
                        <ChevronLeftIcon />
                        <FormattedMessage defaultMessage={'Back'} id={'back.text'} />
                    </Flex>
                </Flex>

                <Divider marginBottom={'16px'} />

                <Flex className={'l1-category-item'} direction={'column'} lineHeight={'20px'}>
                    {menuItems.map((menuItem, index) => {
                        return (
                            <>
                                {menuItem?.isExternal ? (
                                    <Button
                                        key={index}
                                        variant={'link'}
                                        color={'white'}
                                        height={'auto'}
                                        textAlign={'left'}
                                        p={'14px 0px'}
                                        fontWeight={'bold'}
                                        textDecoration={'none !important'}
                                        m={0}
                                        display={'block'}
                                        onClick={() => {
                                            mainMenuItemSelected('', segmentContext)
                                            if (menuItem?.to) window.open(menuItem.to, '_blank')
                                        }}
                                    >
                                        <Text className="link-text-image-title">
                                            {menuItem.message}
                                        </Text>
                                    </Button>
                                ) : (
                                    <Link
                                        key={isAccountMenu ? index : menuItem?.id}
                                        as={RouteLink}
                                        to={
                                            isAccountMenu
                                                ? menuItem.to
                                                : categoryUrlBuilder(menuItem)
                                        }
                                        p={'14px 0px'}
                                        onClick={() => {
                                            if (isAccountMenu) {
                                                navigate(menuItem.path)
                                                setShowAccountMenu(false)
                                            }
                                            mainMenuItemSelected(menuItem, segmentContext)
                                        }}
                                        _active={{
                                            color: 'orangeBaseColor'
                                        }}
                                    >
                                        {isAccountMenu ? (
                                            <Text className="link-text-image-title">
                                                {menuItem.message}
                                            </Text>
                                        ) : (
                                            menuItem?.name
                                        )}
                                    </Link>
                                )}
                            </>
                        )
                    })}
                    <Button
                        onClick={() => {
                            onSocialLoginLogout()
                            handleMenuClick()
                        }}
                        key={'sso-logout'}
                        variant={'link'}
                        color={'white'}
                        height={'auto'}
                        p={'14px 0px'}
                        fontWeight={'bold'}
                        textAlign={'left'}
                        textDecoration={'none !important'}
                        m={0}
                        display={'block'}
                    >
                        <Text className="link-text-image-title">
                            <FormattedMessage id="header.order_status" defaultMessage="Log Out" />
                        </Text>
                    </Button>
                </Flex>
            </Flex>
        )
    }

    return (
        <nav
            id="mobile-list-menu"
            aria-label={intl.formatMessage({
                id: 'list_menu.nav.assistive_msg',
                defaultMessage: 'Main navigation'
            })}
            aria-live="polite"
            aria-busy={ariaBusy}
            aria-atomic="true"
            style={{position: 'relative'}}
        >
            <Flex
                justify="space-between"
                width={'375x'}
                direction={'column'}
                color={'listMenuColor'}
            >
                {isMobile && (
                    <Flex
                        className="svod-link-block-mobile"
                        alignItems={'center'}
                        borderBottom={'1px solid hsla(0, 0%, 100%, .3137254902)'}
                        margin={'0 32px'}
                        padding={'12px 0 20px'}
                        mb={'8px'}
                    >
                        <GlobalContentSlotHtml
                            slotId={'svod-link'}
                            context={'global'}
                            type={'html'}
                        />
                    </Flex>
                )}
                {customer?.isRegistered ? (
                    <HStack
                        padding={'14px 0 22px 0'}
                        margin={'0 32px'}
                        marginBottom={'8px'}
                        borderBottom={'1px solid hsla(0, 0%, 100%, .3137254902)'}
                        className={`mobile-login-button mobilelistMenuItemTrigger ${
                            showAccountMenu ? 'show' : ''
                        }`}
                        justifyContent={'space-between'}
                        onClick={() => {
                            handleAccountMenuToggle(true)
                            mainMenuItemSelected(segmentContext)
                        }}
                        style={{cursor: 'pointer'}}
                    >
                        <Flex alignItems={'center'} fontWeight={'bold'}>
                            <UserIcon />
                            <Text className="link-text-image-title" ml="10px">
                                <FormattedMessage
                                    id="header.popover.title.my_account"
                                    defaultMessage="My Account"
                                />
                            </Text>
                        </Flex>
                        <Box className="right-arrow-icon">
                            <ChevronRightIcon />
                        </Box>
                    </HStack>
                ) : (
                    <HStack
                        padding={'14px 0 22px 0'}
                        margin={'0 32px'}
                        marginBottom={'8px'}
                        fontWeight={'bold'}
                        borderBottom={'1px solid hsla(0, 0%, 100%, .3137254902)'}
                        className="mobile-login-button"
                        onClick={() => {
                            if (ssoEnable) onSocialLoginClick()
                            else navigate('/login')
                            mainMenuItemSelected(segmentContext)
                        }}
                    >
                        <UserIcon />
                        <Text className="link-text-image-title">
                            {intl.formatMessage({
                                id: 'button.login',
                                defaultMessage: 'Log In'
                            })}
                        </Text>
                    </HStack>
                )}

                {customer?.isRegistered && renderSlideMenu(true)}

                {items && (
                    <Stack
                        direction={'column'}
                        width={'375x'}
                        spacing={0}
                        color={'listMenuColor'}
                        {...baseStyle.stackContainer}
                    >
                        {items?.map?.((item) => {
                            const {id, name} = item
                            const itemsCount = item[itemsCountKey] || item[itemsKey]?.length || 0

                            return (
                                <Box key={id} className={'mobilelistmenuItem'}>
                                    {itemsCount > 0 ? (
                                        <Box>
                                            <HStack
                                                className={`mobilelistMenuItemTrigger ${
                                                    activeCategoryId == id ? 'show' : ''
                                                }`}
                                                justifyContent={'space-between'}
                                                onClick={() => {
                                                    handleOpenModal(item)
                                                    mainMenuItemSelected(item, segmentContext)
                                                }}
                                                style={{cursor: 'pointer', padding: '14px 32px'}}
                                            >
                                                <MobileListMenuPopover
                                                    maxColumns={maxColumns}
                                                    item={item}
                                                    name={name}
                                                    items={item?.[itemsKey]}
                                                    itemsKey={itemsKey}
                                                    contentComponent={contentComponent}
                                                />
                                                <ChevronRightIcon />
                                            </HStack>
                                            {renderSlideMenu(false, item)}
                                        </Box>
                                    ) : (
                                        <Link
                                            as={RouteLink}
                                            to={categoryUrlBuilder(item)}
                                            onMouseOver={setActiveLink.bind(this, id)}
                                            onMouseOut={setActiveLink.bind(this)}
                                            {...baseStyle.listMenuTriggerLink}
                                            {...{name: name + ' __'}}
                                            {...(activeLink === id
                                                ? baseStyle.listMenuTriggerlessLinkActive
                                                : {})}
                                            className="level-1-link"
                                            padding={'14px 32px'}
                                            margin={0}
                                        >
                                            {name}
                                        </Link>
                                    )}
                                </Box>
                            )
                        })}
                    </Stack>
                )}
            </Flex>
        </nav>
    )
}

MobileListMenu.displayName = 'MobileListMenu'

MobileListMenu.propTypes = {
    root: PropTypes.object,
    /**
     * The maximum number of columns that we want to use per row in the menu.
     */
    maxColumns: PropTypes.number,
    /**
     * Customize the property representing the items.
     */
    itemsKey: PropTypes.string,
    /**
     * Cusomtize the property representing the items count.
     */
    itemsCountKey: PropTypes.string,
    /**
     * Customize the component used to render the list menu item
     */
    contentComponent: PropTypes.elementType,
    isOpen: PropTypes.bool,
    handleMenuClick: PropTypes.func
}

export {MobileListMenu}
